// FE-SCRM-048 — Step 3: plain-language commit preview. Producer
// confirms the import volume + any warning trade-offs before we write
// the rows. Triggers BE-SCRM-047's commit endpoint on Confirm.

import { Button } from "@/design-system";

import type { ImportSession } from "./types";


export interface CommitPreviewStepProps {
  session: ImportSession;
  on_confirm: () => void;
  on_cancel: () => void;
  is_committing: boolean;
}


export function CommitPreviewStep({
  session, on_confirm, on_cancel, is_committing,
}: CommitPreviewStepProps) {
  const report = session.validation_report;
  const valid_count = report?.valid_count ?? 0;
  const warning_count = report?.warning_count ?? 0;
  const subject = session.import_type === "prospects"
    ? (valid_count === 1 ? "prospect" : "prospects")
    : (valid_count === 1 ? "sales contact" : "sales contacts");

  return (
    <div
      data-testid="import-step-commit-preview"
      className="flex flex-col gap-5"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Ready to import
        </h2>
        <p className="text-xs text-text-secondary">
          Confirm to create these rows. This is the first time data is
          written.
        </p>
      </header>

      <div
        className={[
          "flex flex-col gap-3 rounded-md border border-border-subtle",
          "bg-raised p-4 text-sm text-text-primary",
        ].join(" ")}
      >
        <p>
          You're about to create{" "}
          <strong className="font-semibold">{valid_count}</strong>
          {" "}{subject}.
        </p>
        {warning_count > 0 && (
          <p className="text-text-secondary">
            <strong className="font-semibold">{warning_count}</strong>
            {" "}row{warning_count === 1 ? "" : "s"} flagged as
            duplicates or low-confidence matches. Those will be skipped
            — you can address them after.
          </p>
        )}
        {session.import_type === "prospects" && (
          <p className="text-xs text-text-tertiary">
            Prospects with a valid EIN will auto-queue for Form 5500
            intelligence matching once they're created.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={on_cancel}
          disabled={is_committing}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={on_confirm}
          loading={is_committing}
        >
          Confirm import
        </Button>
      </div>
    </div>
  );
}
